import React from 'react';

/**
 * FlashingProfileSVG — pure-render helper that draws a fold-by-fold
 * side-profile inside a fixed-size SVG box. Used by:
 *   - FlashingFoldsPanel (per-line preview on Components tab)
 *   - FlashingTemplatesPage (library card thumbnails)
 *   - FlashingTemplatePicker (quick-pick dropdown rows)
 *
 * Layout modes:
 *   - 'perpendicular' (default) — labels float next to each segment
 *     offset along its perpendicular. Best for the trace editor live
 *     preview where the canvas is large.
 *   - 'right-column' — polyline scales into the left ~58% of the SVG
 *     and labels stack as a clean vertical legend in the right ~42%
 *     with a faint dashed leader line back to each segment midpoint.
 *     Best for library card thumbnails where overlapping pills muddy
 *     a tight thumbnail.
 *
 * Geometry mirrors `_build_fold_polyline` on the backend so the on-screen
 * preview is pixel-equivalent to the PDF render.
 */
export default function FlashingProfileSVG({
  folds = [],
  startAngle = 0,
  width = 220,
  height = 110,
  showLabels = true,
  labelsLayout = 'perpendicular',
  strokeColor = '#16A34A',
  fillBg = '#FAFAF9',
  borderColor = '#E2E8F0',
  edges = null,            // { start: 'SoftEdge' | null, end: 'SoftEdge' | null }
  edgeConfigs = null,      // { start: {angle_deg, size, tail, under, back}, end: {...} }
}) {
  const safeFolds = (Array.isArray(folds) ? folds : []).filter(
    (f) => Number(f?.length_mm) > 0
  );
  if (safeFolds.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-[10px] text-slate-400 italic"
        style={{ width, height, background: fillBg, border: `1px dashed ${borderColor}` }}
      >
        No folds yet
      </div>
    );
  }

  // Walk the fold list into a series of point coordinates in real-world
  // units (mm). Heading starts at `startAngle` deg and rotates by each
  // fold's bend_angle in its bend_dir.
  let x = 0;
  let y = 0;
  let heading = ((Number(startAngle) || 0) * Math.PI) / 180;
  const pts = [[x, y]];
  safeFolds.forEach((f) => {
    const len = Number(f.length_mm);
    x += Math.cos(heading) * len;
    y += Math.sin(heading) * len;
    pts.push([x, y]);
    const angle = Number(f.bend_angle ?? 90);
    const sign = f.bend_dir === 'ccw' ? -1 : 1;
    heading += sign * ((angle * Math.PI) / 180);
  });

  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const w = Math.max(1, maxX - minX);
  const h = Math.max(1, maxY - minY);

  const useRightColumn = showLabels && labelsLayout === 'right-column';

  // Reserve the right ~42% of the SVG for the label legend in
  // right-column mode so the polyline never overlaps its own dimensions.
  // The polyline drawing area shrinks accordingly.
  const RIGHT_COL_W = useRightColumn ? Math.max(86, Math.min(140, Math.round(width * 0.42))) : 0;
  const drawW = width - RIGHT_COL_W;

  // Reserve extra padding around the polyline when SoftEdge is fitted
  // at either end — the new SE glyph extends a long tail OUT past the
  // endpoint plus a small dip UNDER the line, so we widen the bbox so
  // the tail never clips against the SVG edge. We only pad the side
  // that actually has SE so small thumbnails don't get squished.
  // Polyline orientation isn't known here, so we widen symmetrically
  // (cheap + always-correct), but the values are modest.
  const seStart = edges && edges.start === 'SoftEdge';
  const seEnd   = edges && edges.end   === 'SoftEdge';
  const hasSE   = seStart || seEnd;
  const SE_TAIL_PX  = 48;     // matches max SE glyph tail in screen px
  const SE_UNDER_PX = 22;     // matches max SE glyph dip below
  const PAD_BASE = useRightColumn ? 10 : (showLabels ? 22 : 16);
  const PAD = hasSE ? Math.max(PAD_BASE, SE_UNDER_PX) : PAD_BASE;
  // Both sides padded when SE present — we don't know orientation, but
  // it's symmetric and cheap.
  const SE_SIDE_PAD = hasSE ? SE_TAIL_PX : 0;
  const drawableW = drawW - PAD * 2 - SE_SIDE_PAD * 2;
  const drawableH = height - PAD * 2 - SE_UNDER_PX;
  const scale = Math.min(drawableW / w, drawableH / h);
  const ox = PAD + SE_SIDE_PAD - minX * scale + (drawableW - w * scale) / 2;
  const oy = PAD - minY * scale + (drawableH - h * scale) / 2;
  const screen = pts.map(([px, py]) => [px * scale + ox, py * scale + oy]);

  // ── Right-column legend layout — one row per fold, evenly stacked
  // vertically inside the reserved right strip.
  const legend = [];
  if (useRightColumn) {
    const n = safeFolds.length;
    const PAD_TOP = 6;
    const PAD_BOT = 6;
    const rowH = Math.min(20, Math.max(13, (height - PAD_TOP - PAD_BOT) / n));
    const FONT = Math.max(9, Math.min(11, Math.round(width / 22)));
    const ROW_X = drawW + 6;            // legend pill left edge
    const PILL_W = RIGHT_COL_W - 12;
    for (let i = 0; i < n; i++) {
      const cy = PAD_TOP + rowH * (i + 0.5);
      const midX = (screen[i][0] + screen[i + 1][0]) / 2;
      const midY = (screen[i][1] + screen[i + 1][1]) / 2;
      legend.push({
        i,
        // Pill position
        x: ROW_X,
        y: cy - rowH * 0.4,
        w: PILL_W,
        h: rowH * 0.85,
        // Text baseline
        textX: ROW_X + 6,
        textY: cy + FONT * 0.35,
        // Leader line from polyline midpoint → left edge of the pill
        leaderX1: midX,
        leaderY1: midY,
        leaderX2: ROW_X - 2,
        leaderY2: cy,
        fontSize: FONT,
        label: `F${i + 1} · ${Math.round(Number(safeFolds[i].length_mm))} mm`,
      });
    }
  }

  return (
    <svg
      width={width}
      height={height}
      style={{ background: fillBg, border: `1px solid ${borderColor}`, borderRadius: 6, display: 'block' }}
    >
      {/* polyline */}
      <polyline
        points={screen.map((p) => p.join(',')).join(' ')}
        fill="none"
        stroke={strokeColor}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* joint dots */}
      {screen.map(([px, py], i) => (
        <circle key={i} cx={px} cy={py} r={2.5} fill={strokeColor} stroke="#fff" strokeWidth={0.6} />
      ))}

      {/* End-of-line edging glyphs (SoftEdge / hemmed / folded edge).
          Drawn AFTER the polyline so the curl tucks neatly over the
          endpoint. Direction is mirrored between start vs end so the
          hook always curls AWAY from the rest of the polyline. */}
      {edges?.start && screen.length >= 2 && (
        <EdgeGlyph
          end="start"
          edgeType={edges.start}
          p0={screen[0]}
          pAdj={screen[1]}
          strokeColor={strokeColor}
          scale={scale}
          config={edgeConfigs?.start || {}}
        />
      )}
      {edges?.end && screen.length >= 2 && (
        <EdgeGlyph
          end="end"
          edgeType={edges.end}
          p0={screen[screen.length - 1]}
          pAdj={screen[screen.length - 2]}
          strokeColor={strokeColor}
          scale={scale}
          config={edgeConfigs?.end || {}}
        />
      )}

      {/* Right-column legend (preferred for tight thumbnails) — keeps
          the polyline pristine and stacks dimensions in a tidy column.
          No leader lines: the operator asked for the previews to read
          cleanly with just the flashing line + a clean dimension list. */}
      {useRightColumn && legend.map((row) => (
        <g key={`leg${row.i}`}>
          <rect
            x={row.x}
            y={row.y}
            width={row.w}
            height={row.h}
            rx={row.h / 2}
            fill="#FFFFFF"
            stroke={strokeColor}
            strokeWidth={0.8}
          />
          <text
            x={row.textX}
            y={row.textY}
            fontSize={row.fontSize}
            fontWeight={600}
            fill="#0F172A"
          >
            {row.label}
          </text>
        </g>
      ))}

      {/* Perpendicular floating pills — the original behaviour. Kept for
          larger preview surfaces (trace editor) where they read fine. */}
      {showLabels && !useRightColumn &&
        safeFolds.map((f, i) => {
          if (i + 1 >= screen.length) return null;
          const [x1, y1] = screen[i];
          const [x2, y2] = screen[i + 1];
          const mx = (x1 + x2) / 2;
          const my = (y1 + y2) / 2;
          const vx = x2 - x1;
          const vy = y2 - y1;
          const segLen = Math.max(1, Math.hypot(vx, vy));
          const nx = -vy / segLen;       // unit perpendicular
          const ny =  vx / segLen;
          // Adapt label offset to thumbnail size — smaller cards need
          // a tighter offset or pills run off the canvas edges.
          const OFF = Math.max(7, Math.min(11, Math.round(width / 22)));
          const labelText = `F${i + 1} · ${Math.round(Number(f.length_mm))} mm`;
          const FONT = Math.max(8, Math.min(11, Math.round(width / 22)));
          const pillW = Math.max(28, labelText.length * (FONT * 0.55) + 6);
          const pillH = FONT + 6;
          let lx = mx + nx * OFF;
          let ly = my + ny * OFF;
          lx = Math.max(pillW / 2 + 1, Math.min(width  - pillW / 2 - 1, lx));
          ly = Math.max(pillH / 2 + 1, Math.min(height - pillH / 2 - 1, ly));
          return (
            <g key={`l${i}`}>
              <rect
                x={lx - pillW / 2}
                y={ly - pillH / 2}
                width={pillW}
                height={pillH}
                rx={pillH / 2}
                fill="#FFFFFF"
                stroke={strokeColor}
                strokeWidth={0.8}
              />
              <text
                x={lx}
                y={ly + FONT * 0.35}
                fontSize={FONT}
                fontWeight={600}
                textAnchor="middle"
                fill="#0F172A"
              >
                {labelText}
              </text>
            </g>
          );
        })}
    </svg>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Edge-type registry — each entry knows how to draw its glyph relative to a
// polyline endpoint. The glyph is a small hook/curl that sits AT the
// endpoint and curls AWAY from the polyline body — i.e. the perpendicular
// "outward" direction. SoftEdge is the only seeded type for now; more
// edge types (hemmed, folded over, drip-edge, etc.) can be plugged in by
// adding a new key + path function to this map.
// ───────────────────────────────────────────────────────────────────────────
export const EDGE_TYPES = [
  { key: 'None',     label: 'None' },
  { key: 'SoftEdge', label: 'SoftEdge (SE)' },
  // Future-friendly: leave room for the office to add others later.
];

function EdgeGlyph({ end, edgeType, p0, pAdj, strokeColor, scale = 1, config = {} }) {
  // `p0` is the endpoint we're decorating; `pAdj` is the adjacent vertex
  // (used to compute the tangent direction so the curl orients correctly).
  if (!edgeType || edgeType === 'None') return null;
  const [x0, y0] = p0;
  const [xa, ya] = pAdj;
  // Unit tangent FROM endpoint INTO the polyline (so we can extend the
  // tail past the endpoint by stepping in the -t direction).
  const dx = xa - x0;
  const dy = ya - y0;
  const len = Math.max(1, Math.hypot(dx, dy));
  let tx = dx / len;          // unit tangent (inward)
  let ty = dy / len;
  // Pick the perpendicular pointing DOWN (under the flashing) by default
  // — matches the operator's hand-drawn SoftEdge convention. Operator
  // can override via `config.hang_dir`:
  //   - 'auto' (default): pick perpendicular pointing DOWN (largest ny)
  //   - 'down' / 'up'   : force ny > 0 / ny < 0
  //   - 'right' / 'left': force nx > 0 / nx < 0
  // Mirror-aware: for a vertical barge flashing, the operator can pick
  // 'left' or 'right' so the curl hangs to the outside of the roof
  // instead of always down.
  const cand1 = { nx:  ty, ny: -tx };
  const cand2 = { nx: -ty, ny:  tx };
  const hangDir = (config.hang_dir || 'auto');
  let pick;
  if (hangDir === 'down') {
    pick = cand1.ny >= cand2.ny ? cand1 : cand2;
  } else if (hangDir === 'up') {
    pick = cand1.ny <= cand2.ny ? cand1 : cand2;
  } else if (hangDir === 'right') {
    pick = cand1.nx >= cand2.nx ? cand1 : cand2;
  } else if (hangDir === 'left') {
    pick = cand1.nx <= cand2.nx ? cand1 : cand2;
  } else {
    // 'auto' — prefer DOWN, tiebreaker RIGHT (off the start-vertex circle)
    pick = cand1.ny > cand2.ny
      ? cand1
      : (cand1.ny === cand2.ny ? (cand1.nx >= cand2.nx ? cand1 : cand2) : cand2);
  }
  let nx = pick.nx;
  let ny = pick.ny;

  // Per-template rotation override — operator can dial the curl any
  // direction (0° = the auto-picked outward orientation, +90° rotates
  // it clockwise on screen, -90° counter-clockwise). Rotates BOTH the
  // tangent + normal vectors so the entire glyph rotates around the
  // anchor (the endpoint).
  const rotDeg = Number(config.angle_deg) || 0;
  if (rotDeg !== 0) {
    const r = (rotDeg * Math.PI) / 180;
    const cs = Math.cos(r);
    const sn = Math.sin(r);
    const tx2 = tx * cs - ty * sn;
    const ty2 = tx * sn + ty * cs;
    const nx2 = nx * cs - ny * sn;
    const ny2 = nx * sn + ny * cs;
    tx = tx2; ty = ty2; nx = nx2; ny = ny2;
  }

  // Glyph size — small but visible. Scaled with the polyline so the curl
  // stays proportional on tiny library thumbnails AND big trace canvases.
  // Capped at 16 px so the SE tail (≈ 2.8 × SIZE = 45 px) fits inside
  // the reserved padding strip without clipping. Operator can override
  // via config.size (treated as a multiplier 0.5 → 2.0).
  const baseSize = Math.max(7, Math.min(16, 8 + scale * 22));
  const sizeMult = Math.max(0.25, Math.min(2.5, Number(config.size) || 1.0));
  const SIZE = baseSize * sizeMult;

  // SoftEdge — a fold-back hem. Operator-tunable shape parameters via
  // `config`. All multipliers default to 1.0 if absent.
  const tailMult  = Math.max(0.25, Math.min(3.0, Number(config.tail)  || 1.0));
  const underMult = Math.max(0.25, Math.min(2.5, Number(config.under) || 1.0));
  const backMult  = Math.max(0.25, Math.min(2.5, Number(config.back)  || 1.0));
  const toScreen = (t, n) => [x0 + tx * t + nx * n, y0 + ty * t + ny * n];
  const TAIL  = SIZE * 2.8 * tailMult;
  const UNDER = SIZE * 0.8 * underMult;
  const BACK  = SIZE * 0.55 * backMult;
  // Under-curl: from endpoint go DOWN and slightly BACK under the line.
  const c1a = toScreen( SIZE * 0.15, SIZE * 0.20);
  const c1b = toScreen( BACK,        SIZE * 0.55);
  const p1  = toScreen( BACK * 0.65, UNDER);
  // Sweep-out tail: from the under-curl turn around and extend OUT past
  // the endpoint with a long graceful curve.
  const c2a = toScreen( BACK * 0.10, UNDER * 1.10);
  const c2b = toScreen(-TAIL * 0.55, UNDER * 0.95);
  const p2  = toScreen(-TAIL,        UNDER * 0.55);
  const d = (
    `M ${x0} ${y0} ` +
    `C ${c1a[0]} ${c1a[1]} ${c1b[0]} ${c1b[1]} ${p1[0]} ${p1[1]} ` +
    `C ${c2a[0]} ${c2a[1]} ${c2b[0]} ${c2b[1]} ${p2[0]} ${p2[1]}`
  );
  // Distinct contrast colour so the SoftEdge curl stands out against
  // the green polyline (operator preference: "different color is good
  // so it stands out"). Teal-cyan reads well on white + green print.
  const SE_COLOR = '#0891B2';
  return (
    <g pointerEvents="none">
      <path
        d={d}
        fill="none"
        stroke={SE_COLOR}
        strokeWidth={2.0}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* "SE" tag — placed above the tail's far end so the operator can
          see at a glance which edge type is fitted without overlapping
          the polyline body. */}
      {edgeType === 'SoftEdge' && (
        <text
          x={x0 + tx * (-TAIL * 1.05) + nx * (UNDER * 0.15)}
          y={y0 + ty * (-TAIL * 1.05) + ny * (UNDER * 0.15) - 2}
          fontSize={Math.max(8, SIZE * 0.55)}
          fontWeight={700}
          fill={SE_COLOR}
          textAnchor="middle"
        >
          SE
        </text>
      )}
    </g>
  );
}
